/**
 * Options (08 §4), kept simple: three volume sliders, the text size, the comfort toggles, the
 * accessibility switches, and the way through to the Controls and Cloud Save screens. Every
 * change is written to the shared `SettingsStore` at once - there is no "apply" - so the app hears
 * it through `subscribe` and the text size or the accent swap changes under the player's eyes.
 */

import './options.css';
import type { Ctx, Screen } from './context';
import { box, button, el, panel } from './dom';
import type { GameSpeed, Settings, TextSize } from './settings';
import { DEFAULT_SETTINGS } from './settings';
import { choiceRow } from './widgets/choiceRow';
import { frame } from './widgets/frame';

type VolumeKey = 'masterVolume' | 'musicVolume' | 'sfxVolume';
type ToggleKey =
  | 'screenShake' | 'flashes' | 'crtFilter' | 'hud1987' | 'noiseRing'
  | 'colourBlind' | 'readableFont' | 'soundPips' | 'runToggle' | 'crawlToggle' | 'sneakToggle';
type ToggleRow = readonly [key: ToggleKey, label: string, note: string];

const VOLUMES: ReadonlyArray<[key: VolumeKey, label: string]> = [
  ['masterVolume', 'MASTER VOLUME'],
  ['musicVolume', 'MUSIC'],
  ['sfxVolume', 'EFFECTS'],
];

const COMFORT: readonly ToggleRow[] = [
  ['screenShake', 'SCREEN SHAKE', 'EXPLOSIONS SHAKE THE PICTURE.'],
  ['flashes', 'FULL FLASHES', 'OFF CAPS FLASH BRIGHTNESS AND RATE (PHOTOSENSITIVITY-SAFE).'],
  ['crtFilter', 'CRT FILTER', 'SCANLINES AND A SLIGHT CURVE.'],
  ['hud1987', '1987 HUD', 'THE ORIGINAL SINGLE BOX, TOP LEFT.'],
  ['noiseRing', 'NOISE RING', 'SHOWS HOW FAR YOUR OWN NOISE CARRIES.'],
];

const ACCESS: readonly ToggleRow[] = [
  ['colourBlind', 'COLOUR-BLIND', 'ENEMY AND ALERT RED BECOME BLUE. MARKS AND WORDS STAY AS THEY ARE.'],
  ['readableFont', 'READABLE TEXT', 'BRIEFINGS AND CITATIONS IN A PLAIN SANS-SERIF, MIXED CASE, LOOSER SPACING.'],
  ['soundPips', 'VISUALISE SOUNDS', 'ON-SCREEN PIPS POINT AT GUNFIRE, SHOUTS, VEHICLES AND AIRCRAFT.'],
];

const HOLDS: readonly ToggleRow[] = [
  ['runToggle', 'RUN IS A TOGGLE', 'OFF: HOLD THE KEY TO RUN.'],
  ['crawlToggle', 'CRAWL IS A TOGGLE', 'OFF: HOLD THE KEY TO STAY DOWN.'],
  ['sneakToggle', 'SNEAK IS A TOGGLE', 'THE CROUCHED WALK: SLOW, QUIET, LOW. OFF: HOLD THE KEY TO KEEP LOW.'],
];

const SPEEDS: ReadonlyArray<{ value: GameSpeed; label: string }> = [
  { value: 1, label: '100%' },
  { value: 0.85, label: '85%' },
  { value: 0.7, label: '70%' },
];

const ON_OFF = [
  { value: true, label: 'ON' },
  { value: false, label: 'OFF' },
];

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function note(text: string): HTMLElement {
  return el('span', 'menu-options-note menu-dim menu-small menu-prose', text);
}

function volumeRow(ctx: Ctx, key: VolumeKey, label: string): HTMLElement {
  const slider = el('input', 'menu-slider menu-grow');
  slider.type = 'range';
  slider.min = '0';
  slider.max = '100';
  slider.step = '5';
  slider.value = String(Math.round(ctx.settings.get()[key] * 100));
  slider.setAttribute('aria-label', label);
  const readout = el('span', 'menu-options-value menu-paper', percent(ctx.settings.get()[key]));
  slider.addEventListener('input', () => {
    const value = Math.min(100, Math.max(0, Number(slider.value) || 0)) / 100;
    const patch: Partial<Settings> = {};
    patch[key] = value;
    ctx.settings.set(patch);
    readout.textContent = percent(value);
  });
  return box('menu-field', el('span', 'menu-field-label', label), slider, readout);
}

function toggleRows(ctx: Ctx, rows: readonly ToggleRow[]): HTMLElement[] {
  return rows.map(([key, label, text]) => {
    const row = choiceRow<boolean>(label, ON_OFF, ctx.settings.get()[key], (value) => {
      const patch: Partial<Settings> = {};
      patch[key] = value;
      ctx.settings.set(patch);
    });
    row.el.append(note(text));
    return row.el;
  });
}

export function optionsScreen(ctx: Ctx): Screen {
  const f = frame('options', 'OPTIONS');
  const sound = panel('SOUND', 'menu-options-panel');
  for (const [key, label] of VOLUMES) sound.body.append(volumeRow(ctx, key, label));

  const display = panel('TEXT AND COMFORT', 'menu-options-panel');
  const sizes: ReadonlyArray<{ value: TextSize; label: string }> = [
    { value: 1, label: 'X1' },
    { value: 1.5, label: 'X1.5' },
    { value: 2, label: 'X2' },
  ];
  const size = choiceRow<TextSize>('TEXT SIZE', sizes, ctx.settings.get().textSize, (textSize) =>
    ctx.settings.set({ textSize }),
  );
  display.body.append(size.el, ...toggleRows(ctx, COMFORT));

  const access = panel('ACCESSIBILITY', 'menu-options-panel');
  const speed = choiceRow<GameSpeed>('GAME SPEED', SPEEDS, ctx.settings.get().gameSpeed, (gameSpeed) =>
    ctx.settings.set({ gameSpeed }),
  );
  speed.el.append(note('THE GROUND PHASE ONLY. MARKED ON THE RECORD; NEVER BLOCKS PROGRESS.'));
  access.body.append(...toggleRows(ctx, ACCESS), speed.el);

  const holds = panel('RUN, CRAWL AND SNEAK', 'menu-options-panel');
  holds.body.append(...toggleRows(ctx, HOLDS));

  const back = button('BACK', () => ctx.nav.title());
  const controls = button('CONTROLS', () => ctx.nav.controls());
  controls.title = 'EVERY ACTION AND ITS KEY; REBIND ANY OF THEM.';
  const cloud = button('CLOUD SAVE', () => ctx.nav.cloud());
  cloud.title = 'THE SAVE CODE: CARRY THIS COMPANY TO ANOTHER DEVICE.';
  const reset = button('DEFAULTS', () => {
    // The permadeath notice is a fact the player has learned, not a preference: keep it.
    const keep: Partial<Settings> = { permadeathSeen: ctx.settings.get().permadeathSeen };
    ctx.settings.set({ ...DEFAULT_SETTINGS, ...keep });
    ctx.nav.options();
  });

  const column = box('menu-col menu-grow menu-scroll', sound.panel, display.panel, access.panel, holds.panel);
  f.body.append(column, box('menu-col menu-options-side', back, controls, cloud, reset));
  return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => back };
}
